'use client';

import { useState, useEffect, useRef } from 'react';
import { clarity } from 'react-microsoft-clarity';
import { GoogleTagManager } from '@next/third-parties/google';
import { X, ChevronDown, Check, Shield, Info } from 'lucide-react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';

interface CookieSettings {
    necessary: boolean;
    analytics: boolean;
    marketing: boolean;
}

const DEFAULT_SETTINGS: CookieSettings = {
    necessary: true,
    analytics: true,
    marketing: true
};

export function CookieConsent() {
    const [isVisible, setIsVisible] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [mounted, setMounted] = useState(false);
    // Determine if we should render GTM/Clarity based on CURRENT state (live update)
    const [consentState, setConsentState] = useState<{
        given: boolean;
        settings: CookieSettings;
    }>({ given: false, settings: DEFAULT_SETTINGS });

    const [tempSettings, setTempSettings] = useState<CookieSettings>(DEFAULT_SETTINGS);

    // References for GSAP
    const bannerRef = useRef<HTMLDivElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const backdropRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setMounted(true);
        // Check for existing consent
        const storedConsent = localStorage.getItem('cookie-consent');
        const storedSettings = localStorage.getItem('cookie-settings');

        if (storedConsent === 'true' || storedConsent === 'custom') {
            // Consent previously given
            const settings = storedSettings ? JSON.parse(storedSettings) : DEFAULT_SETTINGS;
            setConsentState({ given: true, settings });

            // Initialize Scripts if allowed
            if (settings.analytics) {
                clarity.init('v5esqd7lky');
            }
        } else if (storedConsent === 'false') {
            // Previously rejected, do nothing
            setConsentState({ given: false, settings: { ...DEFAULT_SETTINGS, analytics: false, marketing: false } });
        } else {
            // No choice made yet
            setIsVisible(true);
        }
    }, []);

    // Animation for Banner
    useGSAP(() => {
        if (isVisible && !showModal && bannerRef.current) {
            gsap.fromTo(bannerRef.current,
                { y: 100, opacity: 0 },
                { y: 0, opacity: 1, duration: 0.5, ease: "power3.out", delay: 1 }
            );
        }
    }, { scope: containerRef, dependencies: [isVisible, showModal] });

    // Animation for Modal
    useGSAP(() => {
        if (showModal) {
            if (backdropRef.current) {
                gsap.fromTo(backdropRef.current, { opacity: 0 }, { opacity: 1, duration: 0.3 });
            }
            if (modalRef.current) {
                gsap.fromTo(modalRef.current,
                    { scale: 0.95, opacity: 0, y: 20 },
                    { scale: 1, opacity: 1, y: 0, duration: 0.4, ease: "back.out(1.1)" }
                );
            }
        }
    }, { scope: containerRef, dependencies: [showModal] });


    const handleAcceptAll = () => {
        const settings = { necessary: true, analytics: true, marketing: true };
        saveConsent('true', settings);
    };

    const handleRejectAll = () => {
        const settings = { necessary: true, analytics: false, marketing: false };
        saveConsent('false', settings);
    };

    const handleSavePreferences = () => {
        saveConsent('custom', tempSettings);
    };

    const saveConsent = (status: string, settings: CookieSettings) => {
        localStorage.setItem('cookie-consent', status);
        localStorage.setItem('cookie-settings', JSON.stringify(settings));

        setConsentState({ given: status !== 'false', settings });

        // Hide UI
        setIsVisible(false);
        setShowModal(false);

        // Google Consent Mode v2 Update
        if (typeof window !== 'undefined' && (window as any).gtag) {
            const consentUpdate = {
                'ad_storage': settings.marketing ? 'granted' : 'denied',
                'ad_user_data': settings.marketing ? 'granted' : 'denied',
                'ad_personalization': settings.marketing ? 'granted' : 'denied',
                'analytics_storage': settings.analytics ? 'granted' : 'denied'
            };
            (window as any).gtag('consent', 'update', consentUpdate);
        }

        // Init scripts if accepted
        if (settings.analytics) {
            clarity.init('v5esqd7lky');
        }
    };

    const toggleSetting = (key: keyof CookieSettings) => {
        if (key === 'necessary') return; // Cannot toggle necessary
        setTempSettings(prev => ({ ...prev, [key]: !prev[key] }));
    };

    if (!mounted) return null;

    return (
        <div ref={containerRef}>
            {/* Render GTM if consent given */}
            {consentState.given && (
                <GoogleTagManager gtmId={process.env.NEXT_PUBLIC_GTM_ID || ""} />
            )}

            {/* Bottom Left Banner (Dark Theme) */}
            {isVisible && !showModal && (
                <div
                    ref={bannerRef}
                    className="fixed bottom-6 left-6 z-50 w-[90vw] max-w-[400px] bg-gray-900 text-white p-6 rounded-xl shadow-2xl border border-gray-800"
                >
                    <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                        <span>We value your privacy</span>
                    </h3>
                    <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                        We use cookies to enhance your browsing experience, serve personalized ads or content, and analyze our traffic. By clicking &quot;Accept All&quot;, you consent to our use of cookies.
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                        <button
                            onClick={() => setShowModal(true)}
                            className="col-span-1 px-4 py-2 text-sm font-medium border border-gray-600 rounded-lg hover:bg-gray-800 transition-colors"
                        >
                            Customize
                        </button>
                        <button
                            onClick={handleRejectAll}
                            className="col-span-1 px-4 py-2 text-sm font-medium border border-gray-600 rounded-lg hover:bg-gray-800 transition-colors"
                        >
                            Reject All
                        </button>
                        <button
                            onClick={handleAcceptAll}
                            className="col-span-2 px-4 py-2 text-sm font-bold bg-green-700 hover:bg-green-600 text-white rounded-lg transition-colors shadow-lg shadow-green-900/20"
                        >
                            Accept All
                        </button>
                    </div>
                </div>
            )}

            {/* Customize Modal (Dark Theme matching Banner) */}
            {showModal && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <div
                        ref={backdropRef}
                        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                        onClick={() => setShowModal(false)}
                    />

                    {/* Modal Content */}
                    <div
                        ref={modalRef}
                        className="relative w-full max-w-2xl bg-gray-900 text-white rounded-2xl shadow-2xl border border-gray-800 overflow-hidden max-h-[90vh] flex flex-col"
                    >
                        {/* Header */}
                        <div className="p-6 border-b border-gray-800 flex justify-between items-center bg-gray-900">
                            <div>
                                <h2 className="text-xl font-bold">Customise Consent Preferences</h2>
                                <p className="text-gray-400 text-sm mt-1">Manage your cookie settings</p>
                            </div>
                            <button
                                onClick={() => setShowModal(false)}
                                className="p-2 hover:bg-gray-800 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Scrollable Content */}
                        <div className="p-6 overflow-y-auto custom-scrollbar space-y-4">
                            <p className="text-sm text-gray-400 mb-6">
                                We use cookies to help you navigate efficiently and perform certain functions. You will find detailed information about all cookies under each consent category below.
                            </p>

                            {/* Necessary */}
                            <div className="border border-gray-800 rounded-xl overflow-hidden">
                                <div className="p-4 bg-gray-800/50 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <ChevronDown className="w-5 h-5 text-gray-400" />
                                        <span className="font-semibold">Necessary</span>
                                    </div>
                                    <span className="text-xs font-bold text-green-500 uppercase tracking-wider">Always Active</span>
                                </div>
                                <div className="p-4 text-sm text-gray-400 bg-gray-900/50 border-t border-gray-800">
                                    Necessary cookies are required to enable the basic features of this site, such as providing secure log-in or adjusting your consent preferences. These cookies do not store any personally identifiable data.
                                </div>
                            </div>

                            {/* Analytics */}
                            <div className="border border-gray-800 rounded-xl overflow-hidden">
                                <div className="p-4 bg-gray-800/50 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <ChevronDown className="w-5 h-5 text-gray-400" />
                                        <span className="font-semibold">Analytics</span>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={tempSettings.analytics}
                                            onChange={() => toggleSetting('analytics')}
                                        />
                                        <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                                    </label>
                                </div>
                                <div className="p-4 text-sm text-gray-400 bg-gray-900/50 border-t border-gray-800">
                                    Analytical cookies are used to understand how visitors interact with the website. These cookies help provide information on metrics such as the number of visitors, bounce rate, traffic source, etc.
                                </div>
                            </div>

                            {/* Marketing */}
                            <div className="border border-gray-800 rounded-xl overflow-hidden">
                                <div className="p-4 bg-gray-800/50 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <ChevronDown className="w-5 h-5 text-gray-400" />
                                        <span className="font-semibold">Marketing</span>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={tempSettings.marketing}
                                            onChange={() => toggleSetting('marketing')}
                                        />
                                        <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                                    </label>
                                </div>
                                <div className="p-4 text-sm text-gray-400 bg-gray-900/50 border-t border-gray-800">
                                    Marketing cookies are used to track visitors across websites. The intention is to display ads that are relevant and engaging for the individual user.
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-6 border-t border-gray-800 bg-gray-900 flex justify-end gap-3 rounded-b-2xl">
                            <button
                                onClick={handleRejectAll}
                                className="px-5 py-2.5 text-sm font-medium border border-gray-600 rounded-lg hover:bg-gray-800 transition-colors"
                            >
                                Reject All
                            </button>
                            <button
                                onClick={handleSavePreferences}
                                className="px-5 py-2.5 text-sm font-medium border border-green-700 text-green-500 rounded-lg hover:bg-green-900/30 transition-colors"
                            >
                                Save My Preferences
                            </button>
                            <button
                                onClick={handleAcceptAll}
                                className="px-6 py-2.5 text-sm font-bold bg-green-700 hover:bg-green-600 text-white rounded-lg transition-colors shadow-lg shadow-green-900/20"
                            >
                                Accept All
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
